import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Card } from "@/components/common";
import { RotateCcw, AlertTriangle, Clock } from "lucide-react";

const FILTERS = [
  { key: "all", label: "Tutte" },
  { key: "scadute", label: "Solo scadute" },
  { key: "scadenza", label: "In scadenza" },
];

export default function OpzioniPrenotazione() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState(params.get("f") === "scadute" ? "scadute" : params.get("f") === "scadenza" ? "scadenza" : "all");
  const [renewDays, setRenewDays] = useState("7");

  const load = () => api.get("/booking-options").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setF = (k) => { setFilter(k); setParams(k === "all" ? {} : { f: k }); };

  const renew = async (id) => {
    const x = parseInt(renewDays, 10);
    if (!x) { toast.error("Inserisci i giorni di rinnovo"); return; }
    try {
      await api.patch(`/bookings/${id}/renew-option`, { days: x });
      toast.success(`Opzione rinnovata di ${x} giorni`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Rinnovo non riuscito"); }
  };

  const filtered = items.filter((o) => filter === "all" ? true : filter === "scadute" ? o.expired : o.expiring);
  const nExpired = items.filter((o) => o.expired).length;
  const nExpiring = items.filter((o) => o.expiring).length;

  return (
    <div>
      <PageHeader title="Opzioni prenotazione" subtitle="Gestione delle opzioni in scadenza e scadute">
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Rinnova di (giorni)</label>
            <input data-testid="opt-renew-days" type="number" min="1" value={renewDays} onChange={(e) => setRenewDays(e.target.value)} className="w-24 px-2 py-1.5 rounded-md border border-slate-300 text-sm" />
          </div>
        </div>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="opt-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`opt-filter-${f.key}`}
            onClick={() => setF(f.key)}
            className={`text-sm font-semibold px-3 py-1.5 rounded-md border ${filter === f.key ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
          >
            {f.label}
            {f.key === "scadute" && nExpired > 0 && <span className="ml-1.5 text-xs">({nExpired})</span>}
            {f.key === "scadenza" && nExpiring > 0 && <span className="ml-1.5 text-xs">({nExpiring})</span>}
            {f.key === "all" && <span className="ml-1.5 text-xs">({items.length})</span>}
          </button>
        ))}
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="opt-table">
            <thead>
              <tr className="text-[11px] uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="text-left px-4 py-2.5">Ospite</th>
                <th className="text-left px-3 py-2.5">Struttura · Camera</th>
                <th className="text-left px-3 py-2.5">Origine</th>
                <th className="text-left px-3 py-2.5">Check-in</th>
                <th className="text-left px-3 py-2.5">Scadenza opzione</th>
                <th className="text-left px-3 py-2.5">Stato</th>
                <th className="text-right px-4 py-2.5">Azione</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.id} data-testid={`opt-row-${o.id}`} className={`border-b border-slate-100 ${o.expired ? "bg-rose-50/50" : o.expiring ? "bg-amber-50/40" : ""}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{o.guest || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-600">{o.structure ? `${o.structure} · ` : ""}{o.property}</td>
                  <td className="px-3 py-2.5 text-slate-500">{o.origine}</td>
                  <td className="px-3 py-2.5 text-slate-600">{o.checkin}</td>
                  <td className="px-3 py-2.5 text-slate-600">{o.expiry || "—"}</td>
                  <td className="px-3 py-2.5">
                    {o.expired ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-300"><AlertTriangle size={11} /> Scaduta</span>
                    ) : o.expiring ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300"><Clock size={11} /> {o.left === 0 ? "Scade oggi" : `Tra ${o.left} gg`}</span>
                    ) : (
                      <span className="text-xs text-slate-400">{o.left != null ? `Tra ${o.left} gg` : "Senza scadenza"}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button data-testid={`opt-renew-${o.id}`} onClick={() => renew(o.id)} className="text-xs font-semibold px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"><RotateCcw size={13} /> Rinnova</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Nessuna opzione in questa categoria</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
